import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaReact, 
  FaNodeJs, 
  FaDatabase,
  FaMobile,
  FaRocket,
  FaShieldAlt,
  FaClock,
  FaUsers,
  FaQuoteLeft,
  FaCloud,
  FaCogs,
  FaChartLine,
  FaStar,
  FaCode,
  FaHeadset,
  FaCheckCircle
} from 'react-icons/fa';
import { SiTypescript, SiNextdotjs, SiMongodb, SiMysql, SiVuedotjs, SiExpress, SiPostgresql, SiDocker, SiAmazon, SiGraphql, SiRedis, SiTailwindcss } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './WebApplication.scss';

const WebApplication: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.webApplication.title;
  const seoDescription = serviceSeoData.webApplication.description;
  const seoKeywords = serviceSeoData.webApplication.keywords;
  const seoUrl = serviceSeoData.webApplication.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Web Application Development Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "Web Application Development, SaaS, Enterprise Web Apps, Custom Web Apps, Cloud-Native Apps, Real-Time Apps, API Development, Analytics Integration",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaReact,
      title: 'Progressive Web Apps',
      description: 'Fast, reliable, and engaging web applications that work offline'
    },
    {
      icon: FaCloud,
      title: 'Cloud-Native Architecture',
      description: 'Scalable applications built for cloud deployment and microservices'
    },
    {
      icon: FaDatabase,
      title: 'Real-time Data',
      description: 'Live data synchronization and real-time updates across all devices'
    },
    {
      icon: FaCogs,
      title: 'Custom APIs',
      description: 'RESTful and GraphQL APIs for seamless data integration'
    },
    {
      icon: FaChartLine,
      title: 'Analytics Integration',
      description: 'Built-in analytics and reporting for data-driven decisions'
    },
    {
      icon: FaRocket,
      title: 'High Performance',
      description: 'Optimized for speed with lazy loading and code splitting'
    }
  ];

  const technologies = [
    { icon: FaReact, name: 'React', color: '#61DAFB' },
    { icon: SiNextdotjs, name: 'Next.js', color: '#000000' },
    { icon: SiTypescript, name: 'TypeScript', color: '#3178C6' },
    { icon: FaNodeJs, name: 'Node.js', color: '#339933' },
    { icon: SiMongodb, name: 'MongoDB', color: '#47A248' },
    { icon: SiMysql, name: 'MySQL', color: '#4479A1' },
    { icon: SiVuedotjs, name: 'Vue.js', color: '#4FC08D' },
    { icon: SiExpress, name: 'Express.js', color: '#000000' },
    { icon: SiPostgresql, name: 'PostgreSQL', color: '#336791' },
    { icon: SiDocker, name: 'Docker', color: '#2496ED' },
    { icon: SiAmazon, name: 'AWS', color: '#FF9900' },
    { icon: SiGraphql, name: 'GraphQL', color: '#E10098' },
    { icon: SiRedis, name: 'Redis', color: '#DC382D' },
    { icon: SiTailwindcss, name: 'Tailwind CSS', color: '#06B6D4' }
  ];

  const benefits = [
    {
      icon: FaShieldAlt,
      title: 'Enterprise Security',
      description: 'Advanced security measures including OAuth, JWT, and encryption'
    },
    {
      icon: FaClock,
      title: 'Agile Development',
      description: 'Iterative development with regular demos and feedback cycles'
    },
    {
      icon: FaUsers,
      title: 'Scalable Solutions',
      description: 'Built to handle growth from startup to enterprise levels'
    },
    {
      icon: FaCode,
      title: 'Clean Code',
      description: 'Well-documented, maintainable code following industry best practices'
    },
    {
      icon: FaHeadset,
      title: '24/7 Support',
      description: 'Round-the-clock technical support and maintenance services'
    },
    {
      icon: FaCheckCircle,
      title: 'Quality Assurance',
      description: 'Comprehensive testing and QA to ensure bug-free deployments'
    }
  ];

  const completedProjects = [
    {
      title: 'E-Commerce Platform',
      description: 'Full-featured online marketplace with real-time inventory, payment gateway integration, and advanced analytics dashboard.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
      image: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=800&h=600&fit=crop',
      metrics: { users: '50K+', transactions: '$2M+', uptime: '99.9%' }
    },
    {
      title: 'SaaS Project Management',
      description: 'Cloud-based project management tool with real-time collaboration, task tracking, and team productivity insights.',
      technologies: ['Next.js', 'TypeScript', 'PostgreSQL', 'AWS'],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
      metrics: { users: '25K+', projects: '100K+', uptime: '99.8%' }
    },
    {
      title: 'FinTech Dashboard',
      description: 'Real-time financial analytics platform with secure transactions, automated reporting, and AI-powered insights.',
      technologies: ['React', 'GraphQL', 'Redis', 'Docker'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
      metrics: { transactions: '1M+', accuracy: '99.9%', speed: '<100ms' }
    }
  ];

  const testimonials = [
    {
      text: "The web application they built for our SaaS platform is incredible. It handles thousands of users seamlessly and the performance is outstanding.",
      author: "David Williams",
      company: "SaaS Startup",
      rating: 5
    },
    {
      text: "From concept to deployment, the team delivered a robust web application that exceeded our expectations. The real-time features are game-changing.",
      author: "Lisa Rodriguez",
      company: "FinTech Company",
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="web application development, custom web app, saas development, enterprise web app, react, node.js, cloud-native, real-time app, api development, analytics, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="web-application">
      {/* Hero Section */}
      <section className="web-application__hero">
        <div className="web-application__hero-content">
          <motion.div
            className="web-application__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="web-application__hero-title">
              Custom <span className="web-application__hero-highlight">Web Application</span> Development
            </h1>
            <h2>Custom SaaS & Enterprise Apps Solutions</h2>
            <p className="web-application__hero-subtitle">
              Build powerful, scalable, and conversion-driven web applications tailored to your business goals. From intuitive UI/UX to high-performance backend systems, App Developer Pro crafts custom solutions that deliver speed, security, and seamless user experiences. Boost your digital presence with modern web technology designed to grow with your business.

            </p>
            <div className="web-application__hero-buttons">
              <Link to="/contact" className="web-application__hero-btn web-application__hero-btn--primary">
                Start Your Project <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="web-application__hero-btn web-application__hero-btn--secondary">
                View My Work
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="web-application__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="web-application__hero-dashboard">
              <div className="web-application__hero-dashboard-header">
                <div className="web-application__hero-dashboard-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>Dashboard.app</span>
              </div>
              <div className="web-application__hero-dashboard-content">
                <div className="web-application__hero-dashboard-sidebar">
                  <div className="web-application__hero-dashboard-nav"></div>
                  <div className="web-application__hero-dashboard-nav"></div>
                  <div className="web-application__hero-dashboard-nav"></div>
                  <div className="web-application__hero-dashboard-nav"></div>
                </div>
                <div className="web-application__hero-dashboard-main">
                  <div className="web-application__hero-dashboard-cards">
                    <div className="web-application__hero-dashboard-card">
                      <div className="web-application__hero-dashboard-card-icon">
                        <FaUsers />
                      </div>
                      <div className="web-application__hero-dashboard-card-content">
                        <div className="web-application__hero-dashboard-card-number">2,847</div>
                        <div className="web-application__hero-dashboard-card-label">Active Users</div>
                      </div>
                    </div>
                    <div className="web-application__hero-dashboard-card">
                      <div className="web-application__hero-dashboard-card-icon">
                        <FaChartLine />
                      </div>
                      <div className="web-application__hero-dashboard-card-content">
                        <div className="web-application__hero-dashboard-card-number">$12.5K</div>
                        <div className="web-application__hero-dashboard-card-label">Revenue</div>
                      </div>
                    </div>
                  </div>
                  <div className="web-application__hero-dashboard-graph">
                    <motion.div 
                      className="web-application__hero-dashboard-bar web-application__hero-dashboard-bar--1"
                      initial={{ height: 0 }}
                      animate={{ height: "60%" }}
                      transition={{ delay: 2, duration: 0.8 }}
                    />
                    <motion.div 
                      className="web-application__hero-dashboard-bar web-application__hero-dashboard-bar--2"
                      initial={{ height: 0 }}
                      animate={{ height: "80%" }}
                      transition={{ delay: 2.2, duration: 0.8 }}
                    />
                    <motion.div 
                      className="web-application__hero-dashboard-bar web-application__hero-dashboard-bar--3"
                      initial={{ height: 0 }}
                      animate={{ height: "45%" }}
                      transition={{ delay: 2.4, duration: 0.8 }}
                    />
                    <motion.div 
                      className="web-application__hero-dashboard-bar web-application__hero-dashboard-bar--4"
                      initial={{ height: 0 }}
                      animate={{ height: "90%" }}
                      transition={{ delay: 2.6, duration: 0.8 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="web-application__features">
        <div className="web-application__section-header">
          <motion.h2
            className="web-application__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What App Developer Pro Builds ?
          </motion.h2>
          <motion.p
            className="web-application__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
          Launch your own next-generation SaaS or enterprise platform with fully customizable, cloud-optimized, AI-powered solutions. We develop secure, high-performance applications that streamline operations, automate workflows, and deliver unmatched scalability—helping your business innovate faster and dominate the market.
          </motion.p>
         
        </div>
        
        <div className="web-application__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="web-application__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="web-application__feature-header">
                  <div className="web-application__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="web-application__feature-title">{feature.title}</h3>
                </div>
                <p className="web-application__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="web-application__technologies">
        <motion.div
          className="web-application__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="web-application__section-title">Technologies We Use</h2>
          <p className="web-application__section-subtitle">
            Modern technology stack for robust web applications
          </p>
        </motion.div>
        <div className="web-application__technologies-grid">
          {technologies.map((tech, index) => {
            const TechIcon = tech.icon;
            return (
              <motion.div
                key={tech.name}
                className="web-application__technology-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div 
                  className="web-application__technology-icon"
                  style={{ '--tech-color': tech.color } as React.CSSProperties}
                >
                  <TechIcon />
                </div>
                <span className="web-application__technology-name">{tech.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="web-application__benefits">
        <div className="web-application__section-header">
          <motion.h2
            className="web-application__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our Web Application Services
          </motion.h2>
        </div>
        <div className="web-application__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="web-application__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="web-application__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="web-application__benefit-title">{benefit.title}</h3>
                <p className="web-application__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Completed Projects Section */}
      <section className="web-application__process">
        <div className="web-application__section-header">
          <motion.h2
            className="web-application__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Completed Latest Projects
          </motion.h2>
          <motion.p
            className="web-application__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Real-world applications delivering measurable business results
          </motion.p>
        </div>
        <div className="web-application__projects-grid">
          {completedProjects.map((project, index) => (
            <motion.div
              key={project.title}
              className="web-application__project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="web-application__project-image">
                <img src={project.image} alt={project.title} />
                <div className="web-application__project-overlay">
                  <div className="web-application__project-tech">
                    {project.technologies.map((tech, idx) => (
                      <span key={idx} className="web-application__project-tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="web-application__project-content">
                <h3 className="web-application__project-title">{project.title}</h3>
                <p className="web-application__project-description">{project.description}</p>
                <div className="web-application__project-metrics">
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div key={key} className="web-application__project-metric">
                      <span className="web-application__project-metric-value">{value}</span>
                      <span className="web-application__project-metric-label">{key}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="web-application__testimonials">
        <div className="web-application__section-header">
          <motion.h2
            className="web-application__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="web-application__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="web-application__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="web-application__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="web-application__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="web-application__testimonial-text">"{testimonial.text}"</p>
              <div className="web-application__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="web-application__faq">
        <div className="container">
          <motion.div
            className="web-application__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About Web Application Development</h2>
            <p>Everything you need to know about building scalable web applications</p>
          </motion.div>
          <div className="web-application__faq-list">
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>What is the difference between a website and a web application?</h3>
              <p>A website is primarily informational with static content, while a web application is interactive software that runs in a browser. Web apps have complex functionality like user authentication, data processing, real-time updates, and database interactions. Examples include Gmail, Trello, and online banking systems.</p>
            </motion.div>
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>How much does custom web application development cost?</h3>
              <p>As compare to market price, App Developer Pro delivers high performance web applications at very low cost. Costs vary significantly based on complexity, at here simple web apps start at only $800-$1,000, mid-level applications range from $1,000-$2,000, and enterprise-level solutions can cost $2,000-$5,000+. Factors include features, integrations, security requirements, scalability needs, and ongoing maintenance.</p>
            </motion.div>
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Which technology stack is best for web applications?</h3>
              <p>The best stack depends on your requirements. Popular options include MERN (MongoDB, Express, React, Node.js), MEAN (Angular instead of React), or Laravel with Vue.js. App Developer Pro analysis your needs for scalability, performance, security, and team expertise to recommend the optimal technology stack for your project.</p>
            </motion.div>
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>How long does it take to develop a web application?</h3>
              <p>App Developer Pro is too fast from others, Where your development timelines vary: Simple applications take 2-3 weeks only, medium complexity apps take 3-5 weeks, and enterprise solutions take 5-8+ weeks. This includes discovery, design, development, testing, and deployment. I always use agile methodology to deliver working features incrementally.</p>
            </motion.div>
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>Do you provide cloud hosting and DevOps services?</h3>
              <p>Yes! App Develoepr Pro offers complete infrastructure setup including cloud hosting (AWS, Azure, Google Cloud), CI/CD pipelines, automated deployments, monitoring, backups, and scaling. I highly ensure your web application is secure, fast, and can handle growing user loads efficiently.</p>
            </motion.div>
            <motion.div
              className="web-application__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Can you migrate my legacy application to modern technology?</h3>
              <p>Absolutely! I specialize in modernizing legacy applications. App Developer Pro assess your current system, create a migration strategy, rebuild with modern frameworks, preserve your data integrity, and ensure zero downtime during the transition. This improves performance, security, and maintainability significantly.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="web-application__cta">
        <motion.div
          className="web-application__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="web-application__cta-title">Ready to Build Your Web Application?</h2>
          <p className="web-application__cta-text">
            Let's transform your business idea into a powerful web application that scales with your growth.
          </p>
          <div className="web-application__cta-buttons">
            <Link to="/contact" className="web-application__cta-btn web-application__cta-btn--primary">
              Get Free Quote <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="web-application__cta-btn web-application__cta-btn--secondary">
              View Portfolio
            </Link>
          </div>
        </motion.div>
      </section>
      </div>
    </>
  );
};

export default WebApplication;
